import { useEffect, useRef, useCallback } from 'react';
import { eventHelpers } from '../services/learningEventsApi';
import { useAuthStore } from '../store/authStore';
import type { LearningEventType } from '../types/learning';
import type { TopicStartData, TopicCompleteData, SelfExplanationSubmitData, NavigationData } from '../types/events';

/**
 * Event tracker instance type (non-hook version with flush)
 */
export interface EventTrackerInstance {
  track: (eventType: string, eventData: any) => Promise<void>;
  trackPageView: (data: { page: string; title?: string }) => void;
  trackTopicStart: (data: TopicStartData) => void;
  trackTopicComplete: (data: TopicCompleteData) => void;
  trackSelfExplanationSubmit: (data: SelfExplanationSubmitData) => void;
  trackNavigation: (data: NavigationData) => void;
  trackError: (errorMessage: string, errorStack?: string, context?: string) => void;
  trackQuizAnswer: (data: {
    quizId: string;
    questionId: string;
    answer: string;
    isCorrect: boolean;
    timeSpent: number;
    topicId?: string;
    courseId?: string;
  }) => void;
  trackQuizComplete: (data: {
    quizId: string;
    score: number;
    totalQuestions: number;
    answers?: any[];
    topicId?: string;
    courseId?: string;
  }) => void;
  flush: () => void;
  sessionId: string;
  isEnabled: boolean;
}

/**
 * Event type mapping from old to new 4W model
 */
const EVENT_TYPE_MAP: Record<string, LearningEventType> = {
  'page_view': 'page_view',
  'topic_start': 'start_topic',
  'topic_complete': 'complete_topic',
  'self_explanation_start': 'write_explanation',
  'self_explanation_submit': 'write_explanation',
  'quiz_answer': 'submit_quiz',
  'quiz_complete': 'submit_quiz',
  'navigation': 'navigate_to_topic',
  'error': 'code_error',
  'session_start': 'start_session',
  'session_end': 'end_session'
};

type NavigationKind = PerformanceNavigationTiming['type'] | undefined;

export const shouldReuseStoredSessionId = (
  existing: string | null,
  navigationType: NavigationKind
): existing is string => Boolean(existing && navigationType === 'reload');

// Reloads keep the tab session. A new navigation with copied sessionStorage
// (for example, a duplicated tab) receives a new session instead.
const generateSessionId = (): string => {
  const storageKey = 'statquest-traffic-session';
  try {
    const existing = window.sessionStorage.getItem(storageKey);
    const navigation = window.performance
      ?.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined;
    if (shouldReuseStoredSessionId(existing, navigation?.type)) return existing;
    const generated = 'session_' + Date.now() + '_' + Math.random().toString(36).substring(2, 15);
    window.sessionStorage.setItem(storageKey, generated);
    return generated;
  } catch {
    return 'session_' + Date.now() + '_' + Math.random().toString(36).substring(2, 15);
  }
};

let sessionId = generateSessionId();
const getPageSessionId = (): string => {
  try {
    const stored = window.sessionStorage.getItem('statquest-traffic-session');
    if (!stored) sessionId = generateSessionId();
  } catch {}
  return sessionId;
};

/**
 * Hook for tracking learning events using the 4W model API
 */
export const useEventTracking = (options?: { enabled?: boolean; debug?: boolean }) => {
  const { enabled = true, debug = false } = options || {};
  const isEnabled = useRef(enabled);

  // Track event using 4W model
  const track = useCallback(async (eventType: string, eventData: any): Promise<void> => {
    if (!isEnabled.current) {
      return Promise.resolve();
    }

    try {
      const newEventType = EVENT_TYPE_MAP[eventType] || eventType as LearningEventType;

      // Handle special event types with dedicated helpers
      switch (eventType) {
        case 'topic_start':
          await eventHelpers.logTopicStart(
            eventData.topicId,
            eventData.courseId,
            eventData.chapterId
          );
          break;

        case 'topic_complete':
          await eventHelpers.logTopicComplete(
            eventData.topicId,
            eventData.courseId,
            eventData.timeSpent
          );
          break;

        case 'quiz_answer':
        case 'quiz_complete':
          await eventHelpers.logQuizSubmit(
            eventData.quizId,
            eventData.answers || [],
            eventData.score,
            eventData.topicId,
            eventData.courseId
          );
          break;

        case 'self_explanation_submit':
          // The persisted /self-explanations operation is server-authoritative.
          break;

        case 'navigation':
          await eventHelpers.logNavigation(
            eventData.fromTopicId,
            eventData.toTopicId
          );
          break;

        case 'session_start':
          await eventHelpers.logSessionStart(getPageSessionId());
          break;

        case 'session_end':
          await eventHelpers.logSessionEnd(getPageSessionId(), eventData.duration);
          break;

        case 'page_view': {
          const userId = useAuthStore.getState().user?.id;
          if (userId) {
            await trackAuthenticatedRouteView(userId, eventData.page);
          }
          break;
        }

        default:
          // Unknown activities are never converted to generic clicks/events.
          break;
      }

      if (debug) {
        console.log('[EventTracker] Tracked:', eventType, '→', newEventType);
      }
    } catch (error: any) {
      // Silently ignore 404 errors (endpoint not implemented on backend)
      // Only log other errors in debug mode
      const is404 = error?.status === 404 || error?.message?.includes('404');
      if (debug && !is404) {
        console.error('[EventTracker] Failed to track event:', eventType, error);
      }
    }
  }, [debug]);

  // Specific tracking methods (backward compatible)
  const trackPageView = useCallback((data: { page: string; title?: string }) => {
    track('page_view', data);
  }, [track]);

  const trackTopicStart = useCallback((data: TopicStartData) => {
    track('topic_start', data);
  }, [track]);

  const trackTopicComplete = useCallback((data: TopicCompleteData) => {
    track('topic_complete', data);
  }, [track]);

  const trackSelfExplanationSubmit = useCallback((data: SelfExplanationSubmitData) => {
    track('self_explanation_submit', data);
  }, [track]);

  const trackNavigation = useCallback((data: NavigationData) => {
    track('navigation', data);
  }, [track]);

  const trackError = useCallback((errorMessage: string, errorStack?: string, context?: string) => {
    track('error', { errorMessage, errorStack, context });
  }, [track]);

  const trackQuizAnswer = useCallback((data: {
    quizId: string;
    questionId: string;
    answer: string;
    isCorrect: boolean;
    timeSpent: number;
    topicId?: string;
    courseId?: string;
  }) => {
    track('quiz_answer', data);
  }, [track]);

  const trackQuizComplete = useCallback((data: {
    quizId: string;
    score: number;
    totalQuestions: number;
    answers?: any[];
    topicId?: string;
    courseId?: string;
  }) => {
    track('quiz_complete', data);
  }, [track]);

  return {
    track,
    trackPageView,
    trackTopicStart,
    trackTopicComplete,
    trackSelfExplanationSubmit,
    trackNavigation,
    trackError,
    trackQuizAnswer,
    trackQuizComplete,
    sessionId: getPageSessionId(),
    isEnabled: isEnabled.current
  };
};

// Singleton instance for easy access
let trackerInstance: EventTrackerInstance | null = null;
let lastAuthenticatedNavigation: string | null = null;

export const canTrackAuthenticatedRouteView = (
  isAuthenticated: boolean,
  userId: string | undefined,
  token: string | null,
  pathname: string
): boolean => Boolean(isAuthenticated && userId && token && pathname !== '/');

export const shouldResetAuthenticatedRouteView = (
  isAuthenticated: boolean,
  userId: string | undefined,
  token: string | null,
  isLoading: boolean,
  hasHydrated: boolean
): boolean => Boolean(
  hasHydrated &&
  !isLoading &&
  !isAuthenticated &&
  !userId &&
  !token
);

/**
 * Shared by the top-level route observer and backward-compatible adapters.
 * The user/path key suppresses StrictMode effects and same-route rerenders,
 * while A → B → A remains three genuine navigations.
 */
export const trackAuthenticatedRouteView = async (
  userId: string,
  pathname: string
): Promise<void> => {
  const navigationKey = `${userId}:${pathname}`;
  if (lastAuthenticatedNavigation === navigationKey) return;
  lastAuthenticatedNavigation = navigationKey;
  try {
    await eventHelpers.logPageView(pathname, getPageSessionId());
  } catch {
    // Analytics delivery must not prevent navigation or expose request data.
    console.warn('[EventTracker] Failed to record authenticated page view');
  }
};

export const resetAuthenticatedRouteView = (): void => {
  lastAuthenticatedNavigation = null;
};

/**
 * Initialize event tracking (non-hook version)
 */
export const initEventTracking = (options?: { enabled?: boolean; debug?: boolean }) => {
  if (typeof window === 'undefined') {
    return null;
  }

  // Fire-and-forget async wrapper
  const trackEvent = (eventType: string, eventData: any): Promise<void> => {
    if (options?.enabled === false) {
      return Promise.resolve();
    }

    // Execute async without awaiting (fire and forget)
    return (async () => {
      try {
        const newEventType = EVENT_TYPE_MAP[eventType] || eventType as LearningEventType;

        if (options?.debug) {
          console.log('[EventTracker] Tracked:', eventType, '→', newEventType);
        }

        // Use the appropriate helper based on event type
        switch (eventType) {
          case 'topic_start':
            await eventHelpers.logTopicStart(eventData.topicId, eventData.courseId);
            break;
          case 'topic_complete':
            await eventHelpers.logTopicComplete(eventData.topicId, eventData.courseId, eventData.timeSpent);
            break;
          case 'quiz_complete':
            await eventHelpers.logQuizSubmit(eventData.quizId, eventData.answers || [], eventData.score);
            break;
          case 'self_explanation_submit':
            // The persisted /self-explanations operation is server-authoritative.
            break;
          case 'navigation':
            await eventHelpers.logNavigation(eventData.fromTopicId, eventData.toTopicId);
            break;
          case 'session_end':
            await eventHelpers.logSessionEnd(getPageSessionId(), eventData.duration);
            break;
          case 'page_view': {
            const userId = useAuthStore.getState().user?.id;
            if (userId) {
              await trackAuthenticatedRouteView(userId, eventData.page);
            }
            break;
          }
          default:
            // Generic - will be handled by default case
            break;
        }
      } catch (error) {
        console.error('[EventTracker] Failed to track event:', eventType, error);
      }
    })();
  };

  const flush = () => {
    // Events are sent immediately in the new implementation
    // This is a no-op for compatibility
  };

  // Create wrapper functions for all tracking methods (fire-and-forget)
  const trackPageView = (data: { page: string; title?: string }) => {
    trackEvent('page_view', data);
  };
  const trackTopicStart = (data: TopicStartData) => {
    trackEvent('topic_start', data);
  };
  const trackTopicComplete = (data: TopicCompleteData) => {
    trackEvent('topic_complete', data);
  };
  const trackSelfExplanationSubmit = (data: SelfExplanationSubmitData) => {
    trackEvent('self_explanation_submit', data);
  };
  const trackNavigation = (data: NavigationData) => {
    trackEvent('navigation', data);
  };
  const trackError = (errorMessage: string, errorStack?: string, context?: string) => {
    trackEvent('error', { errorMessage, errorStack, context });
  };
  const trackQuizAnswer = (data: {
    quizId: string;
    questionId: string;
    answer: string;
    isCorrect: boolean;
    timeSpent: number;
    topicId?: string;
    courseId?: string;
  }) => {
    trackEvent('quiz_answer', data);
  };
  const trackQuizComplete = (data: {
    quizId: string;
    score: number;
    totalQuestions: number;
    answers?: any[];
    topicId?: string;
    courseId?: string;
  }) => {
    trackEvent('quiz_complete', data);
  };

  trackerInstance = {
    track: trackEvent,
    trackPageView,
    trackTopicStart,
    trackTopicComplete,
    trackSelfExplanationSubmit,
    trackNavigation,
    trackError,
    trackQuizAnswer,
    trackQuizComplete,
    flush,
    sessionId: getPageSessionId(),
    isEnabled: options?.enabled !== false
  };

  return trackerInstance;
};

/**
 * Get the global event tracker instance
 */
export const getEventTracker = () => {
  return trackerInstance;
};

/**
 * Utility: Track code execution
 */
export const trackCodeExecution = async (
  code: string,
  output: string,
  error?: string,
  topicId?: string,
  courseId?: string
) => {
  await eventHelpers.logCodeExecution(code, output, error, topicId, courseId);
};

/**
 * Utility: Track AI help request
 */
export const trackAIHelpRequest = async (
  question: string,
  code?: string,
  topicId?: string,
  courseId?: string
) => {
  await eventHelpers.logAIHelpRequest(question, code, topicId, courseId);
};
